import classNames from "classnames";
import { DefaultTFuncReturn } from "i18next";
import { MouseEventHandler, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useScreenSize } from "../../hooks/useScreenSize";
import { LeftArrowIcon } from "../../icons/LeftArrowIcon/LeftArrowIcon";
import { ReturnArrowIcon } from "../../icons/ReturnArrowIcon/ReturnArrowIcon";
import { ThreeDotsIcon } from "../../icons/ThreeDotsIcon/ThreeDotsIcon";
import { RootState } from "../../store";
import { checkIsRunning } from "../../store/clock-slice";
import { CustomButton } from "../CustomButton/CustomButton";
import Modal from "../Modal/Modal";

import styles from "./back-button-header.module.css";

type ButtonIcon = "return" | "left";

type BackButtonHeaderProps = {
  handleBackButtonClicked: MouseEventHandler<HTMLImageElement>;
  handleFetchDocument?: MouseEventHandler<HTMLButtonElement>;
  handleShareDefect?: MouseEventHandler<HTMLButtonElement>;
  backButtonForModal?: boolean;
  isDeficiency?: boolean;
  isEditedByCustomer?: boolean;
  showDocumentation?: boolean;
  shareDefect?: boolean;
  icon?: ButtonIcon;
  text?: DefaultTFuncReturn;
};

export const BackButtonHeader = ({
  handleBackButtonClicked,
  handleFetchDocument,
  handleShareDefect,
  backButtonForModal = false,
  isDeficiency = false,
  isEditedByCustomer = false,
  showDocumentation = true,
  shareDefect = false,
  icon = "return",
  text,
}: BackButtonHeaderProps): JSX.Element => {
  const [t] = useTranslation();
  const user = useSelector((state: RootState) => state.user.currentUser.user);
  const appointment = useSelector((state: RootState) => state.appointment);

  const isRunning = useSelector((state: RootState) =>
    checkIsRunning(state, appointment.id, user!.id)
  );

  const isCustomerAdmin = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );
  const { isDesktop } = useScreenSize();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const classes = classNames(styles["back-header"], {
    [styles["back-header__green"]]: isRunning && !backButtonForModal,
    [styles["back-header__white"]]: !isRunning,
    [styles["back-header__white--modal"]]: isRunning && backButtonForModal,
  });

  const handleCancel = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (handleShareDefect) {
      await Promise.resolve(handleShareDefect(event));
    }
    setIsModalOpen(false);
  };

  return (
    <div className={classes}>
      <Modal
        isOpen={isModalOpen}
        cancelText={"ShareDefect"}
        message={" "}
        confirmText={!isEditedByCustomer ? "DownloadDocumentation" : undefined}
        onCancel={handleCancel}
        iconType="share"
        adjustedWidth="432px"
        onConfirm={!isEditedByCustomer ? handleFetchDocument : undefined}
        closeButtonColor="#3D5170"
        confirmButtonColor="#11A8B8"
        backButtonShow={() => {
          setIsModalOpen(false);
        }}
      />

      <div>
        {icon === "return" ? (
          <ReturnArrowIcon handleBackButtonClicked={handleBackButtonClicked} />
        ) : (
          <div className={styles["back__title"]}>
            <LeftArrowIcon handleBackButtonClicked={handleBackButtonClicked} />
            {text && <span>{text}</span>}
          </div>
        )}

        {isCustomerAdmin && isDesktop && isDeficiency && (
          <p className={styles["deficiency-type__title"]}>
            {t("CreateDeficiency")}
          </p>
        )}
      </div>

      {isDesktop &&
        !isEditedByCustomer &&
        showDocumentation &&
        !shareDefect && (
          <div className={styles["back-header__download"]}>
            <p>{t("DownloadDocumentation")}</p>
            <CustomButton
              color="gray"
              icon="document"
              width={48}
              onClick={handleFetchDocument}
            />
          </div>
        )}

      {isDesktop && shareDefect && !showDocumentation && (
        <div className={styles["back-header__download"]}>
          <p>{t("ShareDefect")}</p>
          <CustomButton
            color="gray"
            icon="share"
            width={48}
            onClick={handleShareDefect}
          />
        </div>
      )}

      {isDesktop && shareDefect && showDocumentation && (
        <div className={styles["back-header__download"]}>
          <ThreeDotsIcon
            type="horizontal"
            onClick={() => setIsModalOpen(true)}
          />
        </div>
      )}
    </div>
  );
};
